import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import './WikiItemDetail.css';
import api from '../API/Api';
import { McIcon } from '../components/CraftingGrid';

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString();
}

function groupHighlights(highlights) {
  return (highlights || []).reduce((groups, highlight) => {
    const key = (highlight.tagLabel || highlight.tag || 'General').trim() || 'General';
    if (!groups[key]) groups[key] = [];
    groups[key].push(highlight);
    return groups;
  }, {});
}

export default function WikiUpdates() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('getwikiversions').then((res) => {
      setUpdates(res.isSuccess ? (res.data || []) : []);
      setLoading(false);
    });
  }, []);

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => window.history.back()}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': '#f0ab20' }}>
            <McIcon type="update" size={48} materialKey={"book"}/>
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{ color: '#f0ab20' }}>Updates</h1>
              <div className="wiki-detail-item-type" style={{ color: '#d4920a' }}>Patch notes and changelog</div>
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Link to="/wiki" className="wh-btn wh-btn-secondary">Back to Wiki</Link>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">
          <StaggerItem className="wiki-detail-main">
            <span className="wiki-detail-label">Latest Updates</span>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {loading ? (
                <div className="wiki-detail-box">Loading updates...</div>
              ) : updates.length === 0 ? (
                <div className="wiki-detail-box">No updates published yet.</div>
              ) : (
                updates.map((update) => (
                  <div key={update.id} className="wiki-detail-box" style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>{update.type}</div>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-primary)' }}>{update.version} - {update.title}</h2>
                        <div style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>{formatDate(update.date)}</div>
                      </div>
                      <div style={{ color: update.published ? '#52c078' : 'var(--text-muted)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        {update.published ? 'Published' : 'Draft'}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: '0.9rem' }}>
                      <section style={{ borderTop: '1px solid var(--border-mid)', paddingTop: '0.9rem' }}>
                        <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>Overview</div>
                        {update.summary ? (
                          <p style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.65 }}>{update.summary}</p>
                        ) : (
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No summary was added for this version.</div>
                        )}
                      </section>

                      {Array.isArray(update.highlights) && update.highlights.length > 0 ? (
                        <section style={{ borderTop: '1px solid var(--border-mid)', paddingTop: '0.9rem' }}>
                          <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>Highlights</div>
                          <div style={{ display: 'grid', gap: '0.9rem' }}>
                            {Object.entries(groupHighlights(update.highlights)).map(([groupName, groupItems]) => (
                              <div key={groupName} style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem' }}>
                                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--teal)', marginBottom: '0.55rem' }}>
                                  {groupName}
                                </div>
                                <ul style={{ margin: 0, paddingLeft: '1.2rem', color: 'var(--text-primary)', display: 'grid', gap: '0.45rem' }}>
                                  {groupItems.map((highlight, index) => (
                                    <li key={index} style={{ lineHeight: 1.55 }}>
                                      {highlight.text}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </section>
                      ) : (
                        <section style={{ borderTop: '1px solid var(--border-mid)', paddingTop: '0.9rem' }}>
                          <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>Highlights</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No highlight entries were added yet.</div>
                        </section>
                      )}

                      <section style={{ borderTop: '1px solid var(--border-mid)', paddingTop: '0.9rem' }}>
                        <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>What This Means</div>
                        <div style={{ color: 'var(--text-secondary)', lineHeight: 1.65, fontSize: '0.92rem' }}>
                          This version page is designed to hold patch notes, balance changes, added content, and custom notes in one place.
                        </div>
                      </section>
                    </div>
                  </div>
                ))
              )}
            </div>
          </StaggerItem>

          <StaggerItem className="wiki-detail-sidebar">
            <div>
              <span className="wiki-detail-label">Quick Links</span>
              <div className="wiki-detail-box" style={{ display: 'grid', gap: '0.65rem' }}>
                <Link to="/wiki/items" style={{ color: 'var(--teal)', textDecoration: 'none' }}>→ Browse Items</Link>
                <Link to="/wiki/mobs" style={{ color: 'var(--teal)', textDecoration: 'none' }}>→ Browse Mobs</Link>
                <Link to="/wiki/blocks" style={{ color: 'var(--teal)', textDecoration: 'none' }}>→ Browse Blocks</Link>
              </div>
            </div>
          </StaggerItem>
        </div>
      </StaggerContainer>
    </div>
  );
}
